import { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { changeRiskScore } from '../../core/_requests'
import { IClientItem } from '../../core/_models'
import { RiskScoreAfterChange, RiskScorePreview } from './RiskScorePreview'
import { SIGNED_INT_INPUT_RE, parseIntegerInput } from '../../core/riskScore.consts'

interface Props {
  show: boolean
  token: string
  /** Автоматический (антифродовый) балл */
  riskPoints: number
  /** Ручная часть целиком: бессрочная плюс временная */
  manualChange: number
  /** Текущая бессрочная корректировка — она же начальное значение поля */
  permanentChange: number
  onClose: () => void
  onSave: (client: IClientItem) => void
}

/**
 * Бессрочная корректировка. Поля срока здесь нет намеренно: срок живёт в отдельной форме
 * временной корректировки, и одно поле, управляющее обеими настройками, — ровно то, что QA
 * назвало неинтуитивным.
 *
 * Поле показывает ТЕКУЩЕЕ значение и правится как абсолют: на бэк уходит `permanentChange`,
 * а не дельта к накопленному.
 */
export const ChangeRiskScoreModal = ({
  show,
  token,
  riskPoints,
  manualChange,
  permanentChange,
  onClose,
  onSave,
}: Props) => {
  const [value, setValue] = useState<string>('')
  const [reason, setReason] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (show) {
      setValue(String(permanentChange))
      setReason('')
      setErrors({})
    }
  }, [show, permanentChange])

  const parsed = parseIntegerInput(value)
  // Временная часть правкой не затрагивается, поэтому в итог она входит без изменений.
  const nextManualChange = parsed === null ? null : manualChange - permanentChange + parsed

  const handleSave = async () => {
    setIsSaving(true)
    setErrors({})

    try {
      const response = await changeRiskScore(token, { permanentChange: parsed as number, reason })

      if (response.data.success && response.data.externalUser) {
        onSave(response.data.externalUser)
        onClose()
      } else if (response.data.errors) {
        setErrors(response.data.errors as Record<string, string>)
      }
    } catch (e: any) {
      const responseErrors = e?.response?.data?.errors
      if (responseErrors) {
        setErrors(responseErrors as Record<string, string>)
      } else {
        setErrors({ general_error: 'Произошла ошибка при сохранении' })
      }
    } finally {
      setIsSaving(false)
    }
  }

  const isFormComplete = parsed !== null && reason.trim().length > 0

  return (
    <Modal
      show={show}
      onHide={() => { if (!isSaving) onClose() }}
      centered
      onEscapeKeyDown={(e) => { if (isSaving) e.preventDefault() }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Бессрочная корректировка риск-балла</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.general_error && (
          <div className='alert alert-danger mb-5'>{errors.general_error}</div>
        )}

        <RiskScorePreview riskPoints={riskPoints} manualChange={manualChange} />

        <div className='fv-row mb-5'>
          <label className='required fs-6 fw-semibold mb-2'>Бессрочная корректировка</label>
          <input
            type='text'
            inputMode='numeric'
            autoComplete='off'
            className={`form-control form-control-solid${errors.permanentChange ? ' is-invalid' : ''}`}
            placeholder='0'
            value={value}
            // Гейт не впускает недопустимый символ вместо того, чтобы вычищать его после ввода:
            // так каретка не прыгает. Знак «минус» разрешён — оценку снижают отрицательным числом.
            onChange={(e) => { if (SIGNED_INT_INPUT_RE.test(e.target.value)) setValue(e.target.value) }}
            disabled={isSaving}
          />
          <div className='form-text text-muted'>
            В поле — текущее значение, целое число. Для снижения оценки укажите отрицательное.
            Временную корректировку это не затрагивает
          </div>
          {errors.permanentChange && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>{errors.permanentChange}</div>
            </div>
          )}
        </div>

        <div className='fv-row mb-0'>
          <label className='required fs-6 fw-semibold mb-2'>Причина изменения</label>
          <input
            type='text'
            className={`form-control form-control-solid${errors.reason ? ' is-invalid' : ''}`}
            placeholder='Укажите причину'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isSaving}
          />
          {errors.reason && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>{errors.reason}</div>
            </div>
          )}
        </div>

        <RiskScoreAfterChange
          riskPoints={riskPoints}
          manualChange={manualChange}
          nextManualChange={nextManualChange}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={isSaving}>
          Отмена
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={isSaving || !isFormComplete}>
          {isSaving ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              Сохранение...
            </>
          ) : (
            'Сохранить'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
